
import {NativeAudio} from '@bttr-nl/native-audio';

export default {
  name: 'NativePlayer',
  props: {
    src: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: '',
    },
    artist: {
      type: String,
      default: '',
    },
    artwork: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isPlaying: false,
      isLoading: false,
      currentTime: 0,
      duration: 0,
      progressTimer: null,
      nativeAudioListenerAdded: false,
    };
  },
  computed: {
    assetId() {
      return `native-player-${this.$options.name}-${this.src.split('/').pop()}`;
    },
    progressPercentage() {
      if (this.duration === 0) {
        return 0;
      }
      return Math.min((this.currentTime / this.duration) * 100, 100);
    },
  },
  beforeDestroy() {
    this.cleanup();
  },
  methods: {
    async togglePlayPause() {
      if (this.isPlaying) {
        await this.pause();
      } else {
        await this.play();
      }
    },
    async play() {
      if (this.isLoading) {
        return;
      }

      this.isLoading = true;

      try {
        // Only preload if asset is not ready
        await this.preloadAudioIfNeeded();
        await NativeAudio.play({assetId: this.assetId});
        await this.setupMediaControls();

        const duration = await NativeAudio.getDuration({assetId: this.assetId});
        this.duration = duration.duration || 0;

        this.isPlaying = true;
        this.startProgressTimer();
        this.$emit('play');
      } catch (error) {
        console.error('Error playing native audio:', error);
        this.$emit('error', error);
      } finally {
        this.isLoading = false;
      }
    },
    async pause() {
      try {
        const assetState = await NativeAudio.isAssetReady({assetId: this.assetId});

        if (assetState.assetState !== 0) {
          // Only stop, don't unload to preserve current position
          await NativeAudio.pause({assetId: this.assetId});
        }

        this.isPlaying = false;
        this.stopProgressTimer();
        this.$emit('pause');
      } catch (error) {
        console.error('Error pausing native audio:', error);
        this.$emit('error', error);
      }
    },
    async preloadAudioIfNeeded() {
      try {
        const assetState = await NativeAudio.isAssetReady({assetId: this.assetId});

        // If asset is ready (state 1 (prepared), 2 (pending), 6 (paused)), no need to preload
        if ([1, 2, 6].includes(assetState.assetState)) {
          console.log('Asset already loaded, skipping preload');
          return;
        }

        // Asset not ready, preload it
        await this.preloadAudio();
      } catch (error) {
        // If isAssetReady fails, try to preload anyway
        console.log('Asset state unknown, preloading...');
        await this.preloadAudio();
      }
    },
    async preloadAudio() {
      try {
        await NativeAudio.preload({
          assetId: this.assetId,
          assetPath: this.src,
          audioChannelNum: 1,
          isUrl: true,
        });

        // Wait a bit for preload to complete
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error('Error preloading audio:', error);
        throw error;
      }
    },
    async setupMediaControls() {
      // Add listener for native media control events (only once)
      if (!this.nativeAudioListenerAdded) {
        NativeAudio.addListener('action', (data) => {
          if (!data.assetId.startsWith('native-player-')) {
            // Ignore non-native player actions
            return;
          }

          if (data.assetId === this.assetId) {
            console.log('Native audio action:', data.action);
            if (data.action === 'pause') {
              this.isPlaying = false;
              this.stopProgressTimer();
              this.$emit('pause');
              this.pause();
            }
            if (data.action === 'play') {
              this.isPlaying = true;
              this.startProgressTimer();
              this.$emit('play');
              this.play();
            }
            if (data.action === 'seekTo') {
              this.handleSeekTo(data);
            }
          }
        });
        this.nativeAudioListenerAdded = true;
      }

      // Setup command center controls
      await NativeAudio.setupCommandCenterControls({
        assetId: this.assetId,
        sessionTag: 'native-player-session',
        notificationChannelId: 'native-player-notification',
        notificationChannelName: 'Audio Player',
        notificationChannelDescription: 'Media controls for audio playback',
        notificationId: 8744,
      });

      // Setup now playing info
      await NativeAudio.setupNowPlaying({
        assetId: this.assetId,
        title: this.title || 'Audio',
        artist: this.artist || 'Unknown Artist',
        image: this.artwork || 'https://picsum.photos/536/354',
      });
    },
    async handleSeekTo(data) {
      this.currentTime = data.time;

      // Update the native audio position
      await NativeAudio.seekTo({assetId: this.assetId, time: this.currentTime});
    },
    onProgressTrackClick(event) {
      if (this.duration === 0) { return; }

      const rect = this.$refs.progressTrack.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, clickX / rect.width));

      this.seekToPercentage(percentage);
    },
    async seekToPercentage(percentage) {
      if (this.duration === 0) { return; }

      const newTime = percentage * this.duration;
      this.currentTime = newTime;

      try {
        await NativeAudio.seekTo({assetId: this.assetId, time: newTime});
      } catch (error) {
        console.error('Error seeking:', error);
      }
    },
    startProgressTimer() {
      if (this.progressTimer) {
        clearInterval(this.progressTimer);
      }

      this.progressTimer = setInterval(async () => {
        if (this.isPlaying) {
          try {
            const result = await NativeAudio.getCurrentTime({assetId: this.assetId});
            this.currentTime = result.currentTime || 0;
          } catch (error) {
            // Ignore getCurrentTime errors for now
          }
        }
      }, 500);
    },
    stopProgressTimer() {
      if (this.progressTimer) {
        clearInterval(this.progressTimer);
        this.progressTimer = null;
      }
    },
    formatTime(seconds) {
      if (!seconds || seconds === 0) {
        return '0:00';
      }

      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.floor(seconds % 60);
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    },
    async cleanup() {
      this.stopProgressTimer();

      if (this.isPlaying) {
        try {
          await NativeAudio.stop({assetId: this.assetId});
        } catch (error) {
          console.error('Error during cleanup:', error);
        }
      }
    },
  },
};
